import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Building2,
  Users,
  ShieldCheck,
  DollarSign,
  Package,
  BarChart3,
  Palette,
  CheckCircle,
  TrendingUp,
  FileText,
  UserCheck,
  CreditCard,
  Settings,
  Check,
  X,
  Bell,
  RefreshCw,
  ClipboardList,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

const BrokerageDashboardMockup = () => {
  return (
    <div className="relative">
      {/* Floating Background Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { Icon: Building2, delay: 0, x: "10%", y: "15%" },
          { Icon: Users, delay: 0.5, x: "85%", y: "20%" },
          { Icon: Package, delay: 1, x: "15%", y: "70%" },
          { Icon: BarChart3, delay: 1.5, x: "80%", y: "75%" },
          { Icon: ShieldCheck, delay: 2, x: "50%", y: "10%" },
          { Icon: Palette, delay: 2.5, x: "90%", y: "50%" },
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.15, 0],
              scale: [0, 1, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: item.delay,
              ease: "easeInOut",
            }}
            className="absolute"
            style={{ left: item.x, top: item.y }}
          >
            <item.Icon className="w-16 h-16 text-blue-600" />
          </motion.div>
        ))}
      </div>

      {/* Main Dashboard Mockup */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden relative z-10"
      >
        <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
              </div>
              <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
                Brokerage Overview Dashboard
              </h3>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
              <Search className="w-4 h-4" />
              <Bell className="w-4 h-4" />
              <div className="w-7 h-7 rounded-full bg-gray-300"></div>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                <CounterAnimation value={8} />
              </div>
              <p className="text-sm text-gray-600">Offices</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-green-600">
                <CounterAnimation value={142} />
              </div>
              <p className="text-sm text-gray-600">Active Agents</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold" style={{ color: "#f58b01" }}>
                <CounterAnimation value={5} />
              </div>
              <p className="text-sm text-gray-600">Approved Vendors</p>
            </Card>
            <Card className="p-4">
              <div className="text-2xl font-bold text-purple-600">
                $<CounterAnimation value={12450} />
              </div>
              <p className="text-sm text-gray-600">Monthly Spend</p>
            </Card>
          </div>

          <div className="space-y-3">
            {[
              { office: "Downtown Office", agents: 35, orders: 12, status: "active" },
              { office: "Westside Branch", agents: 28, orders: 8, status: "active" },
              { office: "North District", agents: 42, orders: 15, status: "active" },
              { office: "Harbor Office", agents: 37, orders: 11, status: "active" },
            ].map((office, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{office.office}</p>
                    <p className="text-sm text-gray-600">
                      {office.agents} agents • {office.orders} active orders
                    </p>
                  </div>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                >
                  Active
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function BrokerageFeaturesPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <BrokerageContent />
    </div>
  );
}

function BrokerageContent() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const painPoints: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "noAgentOversight",
      title: "No Agent Oversight",
      description:
        "Agents ordering from multiple vendors with no oversight — scattered communication, inconsistent pricing, and missed deadlines.",
    },
    {
      icon: "lostInventory",
      title: "Lost Inventory",
      description:
        "No visibility over sign inventory or condition — brokerages can't track how many signs are deployed, where they are, or whether they're damaged.",
    },
    {
      icon: "brandInconsistency",
      title: "Brand Inconsistency",
      description:
        "Different offices use different vendors and designs — unauthorized layouts or colors dilute the brokerage's brand identity.",
    },
    {
      icon: "invoiceChaos",
      title: "Invoice Chaos",
      description:
        "Hours wasted reconciling invoices and payments — accounting teams deal with messy trails of orders, emails, and spreadsheets.",
    },
    {
      icon: "noVisibility",
      title: "No Visibility",
      description:
        "No clear visibility into installations, removals, or costs — updates often come only when problems occur.",
    },
    {
      icon: "coordinationOverload",
      title: "Coordination Overload",
      description:
        "Managers caught between vendor coordination and agent requests — constant back-and-forth eats up valuable time.",
    },
    {
      icon: "fileChaos",
      title: "File Chaos",
      description:
        "Design files and branding updates shared over email are often misplaced, with no security or version control.",
    },
    {
      icon: "uncontrolledOperationalSpend",
      title: "Uncontrolled Operational Spend",
      description:
        "Without centralized operations, brokerages overpay for signs, rush jobs, and repeated replacements — driving up marketing spend.",
    },
  ];

  const coreFeatures: { icon: IconName; title: string; color: string; features: string[] }[] = [
    {
      icon: "officeAgentManagement",
      title: "Office & Agent Management",
      color: "text-blue-600 bg-blue-100",
      features: [
        "Manage multiple offices with a clear organizational structure.",
        "Assign office managers to handle orders and service requests.",
        "Keep a live directory of all agents tied to their office for accountability.",
        "Transfer or reassign agents seamlessly, with complete order history preserved.",
      ],
    },
    {
      icon: "thirdVendor",
      title: "Vendor & Service Oversight",
      color: "text-purple-600 bg-purple-100",
      features: [
        "Approve which vendors are available.",
        "Show agents only pre-approved catalogs and pricing.",
        "Standardize sign types, pricing, and workflows.",
        "Platform-Negotiated Savings: access exclusive vendor discounts (e.g., 5% off signs via Farmer Signs) with more partners added.",
      ],
    },
    {
      icon: "centralizedBilling",
      title: "Centralized Billing & Payments",
      color: "text-green-600 bg-green-100",
      features: [
        "Brokerage-billed or office-billed modes.",
        "Bulk payments across many orders.",
        "Auto invoices with built-in tax compliance.",
        "Discounts auto-apply at checkout and reflect on invoices to prevent disputes.",
      ],
    },
    {
      icon: "inventoryManagement",
      title: "Inventory Visibility & Control",
      color: "bg-orange-50",
      features: [
        "Track real estate signage inventory across offices and agents in real time.",
        "Flag damaged or outdated signs for replacement.",
        "Approve or deny bulk purchase requests before they're placed.",
        "Use restock alerts and usage insights to avoid shortages.",
      ],
    },
    {
      icon: "reportingInsight",
      title: "Reporting & Insights",
      color: "text-teal-600 bg-teal-100",
      features: [
        "Dashboards display installs completed, removals pending, and costs incurred.",
        "Break down spend by office, agent, or vendor for true financial clarity.",
        "Vendor performance reports highlight turnaround times, error rates, and accuracy.",
        "Export or schedule reports for leadership and compliance reviews.",
      ],
    },
    {
      icon: "brandConsistency",
      title: "Branding Consistency",
      color: "text-pink-600 bg-pink-100",
      features: [
        "Lock approved templates, logos, and brand guidelines.",
        "Enforce brand compliance across all offices and agents.",
        "Prevent rogue orders and off-brand signage.",
        "Ensure every listing projects a consistent brokerage identity.",
      ],
    },
    {
      icon: "workdriveBrokerage",
      title: "WorkDrive for Brokerages",
      color: "text-indigo-600 bg-indigo-100",
      features: [
        "Central File Hub: Upload, organize, and secure brand files, sign templates, and marketing assets.",
        "Secure Vendor Sharing: Share files directly in workflows with password protection, expiry limits, and permissions.",
        "Team Folders: Enable collaboration between agents and managers with shared folders.",
        "Activity Visibility: See when files were viewed, downloaded, or edited.",
        "Access Control: Decide who can view, edit, or download files.",
      ],
    },
  ];

  const outcomes: { icon: IconName; title: string; description: string }[] = [
    { icon: "saveTime", title: "Save Time", description: "Reduce admin work with automation" },
    {
      icon: "cutCost",
      title: "Cut Costs",
      description: "Consolidated orders reduce waste and unlock exclusive vendor discounts.",
    },
    { icon: "fasterLaunches", title: "Faster Launches", description: "Listings go live on time, every time" },
    {
      icon: "reduceErrors",
      title: "Reduced Errors",
      description: "Minimize missed installs and duplicates",
    },
    { icon: "financialVisibility", title: "Financial Visibility", description: "Track spend at every level" },
    { icon: "vendorAccountability", title: "Vendor Accountability", description: "Monitor performance and SLAs" },
    {
      icon: "consistentBrand",
      title: "Consistent Branding",
      description: "Lock in brokerage-wide standards",
    },
    {
      icon: "smartCollaboration",
      title: "Smarter Collaboration",
      description: "Share designs securely via WorkDrive",
    },
    { icon: "empowerAgent", title: "Empower Agents", description: "Agents order with confidence" },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Background Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Building2, delay: 0, x: "10%", y: "15%" },
            { Icon: Users, delay: 0.5, x: "85%", y: "20%" },
            { Icon: DollarSign, delay: 1, x: "15%", y: "70%" },
            { Icon: BarChart3, delay: 1.5, x: "80%", y: "75%" },
            { Icon: ShieldCheck, delay: 2, x: "50%", y: "10%" },
            { Icon: CreditCard, delay: 2.5, x: "90%", y: "50%" },
            { Icon: TrendingUp, delay: 3, x: "25%", y: "45%" },
            { Icon: FileText, delay: 3.5, x: "70%", y: "40%" },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.3, 0],
                scale: [0, 1, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut",
              }}
              className="absolute"
              style={{ left: item.x, top: item.y }}
            >
              <item.Icon className="w-16 h-16 text-blue-600" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    Bring Order to Brokerage Operations.
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    RealEsy is a powerful brokerage operations platform that gives real estate
                    brokerages full visibility and control over agents, offices, vendors, inventory,
                    billing, and branding — available on both web and mobile.
                  </p>
                </div>

                {/* Partner Offer Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="max-w-3xl mx-auto space-y-4"
                >
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-6 shadow-sm">
                    <div className="text-center space-y-3">
                      <h3 className="font-bold">
                        <span className="bg-green-600 text-white px-3 py-1.5 rounded-md">
                          Exclusive Partner Offer
                        </span>
                      </h3>
                      <p className="text-lg font-medium text-gray-800 leading-relaxed">
                        Get <span className="text-green-600 font-bold">5% off</span> all sign orders
                        from <span className="font-semibold text-gray-900">Farmer Signs</span> when
                        you purchase through RealEsy.
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 text-center">
                    More vendor options coming soon, along with the ability to invite your preferred
                    partners to RealEsy.
                  </p>
                </motion.div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                    >
                      Get Started
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <BrokerageDashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Brokerage Pain Points
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                The challenges brokerages face when managing signage operations without a unified
                system.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {painPoints.map((pain, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="p-6 rounded-2xl border-2 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-red-500 rounded-xl shadow-md border border-gray-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={pain.icon} size={32} alt={pain.title} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{pain.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{pain.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Core Features Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">Core Features</h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Comprehensive tools to manage every aspect of your brokerage operations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={36} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Mobile App for Brokerages */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Text content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center">
                      <AppIcon name="mainMobilePhone" size={32} alt="Mobile App" />
                    </div>
                    <h2 className="text-2xl md:text-4xl font-bold text-gray-900">
                      Mobile App for Brokerages
                    </h2>
                  </div>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Manage your entire brokerage operation from anywhere with our comprehensive
                    mobile app designed for on-the-go efficiency.
                  </p>
                </div>

                <div className="space-y-6">
                  {(
                    [
                      {
                        icon: "goOnManagement",
                        title: "On-the-Go Management",
                        description:
                          "Office managers can approve installs, removals, and orders right from the app.",
                      },
                      {
                        icon: "realTimeUpdates",
                        title: "Real-Time Updates",
                        description: "Push notifications for job status, delays, and completions.",
                      },
                      {
                        icon: "inventoryField",
                        title: "Inventory in the Field",
                        description: "Check sign allocation and condition instantly.",
                      },
                      {
                        icon: "vendorFileSharing",
                        title: "Vendor & File Sharing",
                        description:
                          "Share branding files securely through WorkDrive, even on mobile.",
                      },
                      {
                        icon: "agentEmpowerment",
                        title: "Agent Empowerment",
                        description:
                          "Agents place orders, check statuses, and access approved catalogs without needing desktop access.",
                      },
                    ] as const
                  ).map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center flex-shrink-0">
                        <AppIcon name={item.icon} size={24} alt={item.title} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 }}
                  className="text-white p-6 rounded-2xl"
                  style={{ background: 'linear-gradient(90deg, #073399 0%, #236DD9 100%)' }}
                >
                  <p className="font-medium text-lg">
                    Empower your team to work efficiently from anywhere — approve orders, track
                    progress, and manage inventory all from your mobile device.
                  </p>
                </motion.div>
              </motion.div>

              {/* Right side: Mobile Phone Mockup */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:pl-8"
              >
                <div className="relative max-w-md mx-auto">
                  {/* Enhanced Background Effects */}
                  <div className="absolute -inset-12 opacity-20">
                    <div className="absolute top-8 left-8 w-24 h-24 bg-purple-400 rounded-full blur-2xl animate-pulse"></div>
                    <div
                      className="absolute bottom-8 right-8 w-28 h-28 bg-blue-400 rounded-full blur-2xl animate-pulse"
                      style={{ animationDelay: "1s" }}
                    ></div>
                    <div
                      className="absolute top-1/2 left-2 w-20 h-20 rounded-full blur-2xl animate-pulse"
                      style={{ background: "#f58b01", animationDelay: "2s" }}
                    ></div>
                  </div>

                  {/* iPhone 17 Pro Simulator Frame */}
                  <motion.div
                    whileHover={{ y: -10, rotateY: 8, rotateX: 5 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative transform-gpu mx-auto w-full max-w-[360px]"
                    style={{ perspective: "1000px" }}
                  >
                    {/* Left side: Action / Silent + Volume Up / Volume Down */}
                    <div
                      className="absolute left-0 top-[88px] -translate-x-[3px] w-[3px] h-[22px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[140px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[190px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />

                    {/* Right side: Power / Side button */}
                    <div
                      className="absolute right-0 top-[160px] translate-x-[3px] w-[3px] h-[64px] bg-[#2c2c2e] rounded-r-[2px] z-30"
                      aria-hidden="true"
                    />

                    <div className="relative bg-[#0b0b0c] rounded-[3.1rem] p-[11px] shadow-[0_25px_60px_rgba(0,0,0,0.35)] transform-gpu ring-1 ring-black/40">
                      <div className="relative bg-[#1a1a1c] rounded-[2.65rem] p-[2px]">
                        {/* Phone Screen */}
                        <div className="bg-[#f5f5f7] rounded-[2.55rem] overflow-hidden relative min-h-[720px]">
                          {/* Status Bar + Dynamic Island */}
                          <div className="relative px-7 pt-4 pb-2.5 flex justify-between items-center z-10">
                            <span className="text-[13px] font-semibold text-gray-900 tracking-tight">
                              9:41
                            </span>
                            <div className="absolute left-1/2 top-2.5 -translate-x-1/2 w-[102px] h-[30px] bg-black rounded-full z-20 flex items-center justify-end pr-3">
                              <motion.div
                                animate={{ scale: [1, 1.15, 1] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="w-2 h-2 rounded-full bg-[#1a1a1a] ring-1 ring-gray-700"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-900">
                              <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor" aria-hidden="true">
                                <rect x="0" y="8" width="2.5" height="4" rx="0.5" />
                                <rect x="4" y="5" width="2.5" height="7" rx="0.5" />
                                <rect x="8" y="2" width="2.5" height="10" rx="0.5" />
                                <rect x="12" y="0" width="2.5" height="12" rx="0.5" />
                              </svg>
                              <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor" aria-hidden="true">
                                <path d="M7.5 2.2c1.9 0 3.6.7 4.9 1.9l-.9.9A5.3 5.3 0 0 0 7.5 3.5 5.3 5.3 0 0 0 3.5 5l-.9-.9A6.9 6.9 0 0 1 7.5 2.2zm0 2.4c1.2 0 2.3.5 3.1 1.2l-.9.9a2.9 2.9 0 0 0-4.4 0l-.9-.9A4.3 4.3 0 0 1 7.5 4.6zM7.5 9.2a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" />
                              </svg>
                              <svg width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
                                <rect x="0.5" y="0.5" width="20" height="11" rx="2.5" stroke="currentColor" strokeOpacity="0.35" />
                                <rect x="2" y="2" width="15" height="8" rx="1.5" fill="currentColor" />
                                <path d="M22 4v4a1.5 1.5 0 0 0 0-4z" fill="currentColor" fillOpacity="0.4" />
                              </svg>
                            </div>
                          </div>

                          {/* Mobile App Content */}
                          <div className="px-5 pb-8 pt-3 space-y-4 bg-[#f5f5f7] min-h-[640px]">
                            {/* Header Card */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                              className="flex items-center justify-between p-5 bg-white rounded-2xl shadow-sm border border-gray-100 mt-14"
                            >
                              <div>
                                <h3 className="font-bold text-[17px] text-gray-900 leading-tight">
                                  Brokerages Dashboard
                                </h3>
                                <div className="flex items-center gap-2 mt-1.5">
                                  <motion.div
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className="w-2 h-2 bg-green-500 rounded-full"
                                  />
                                  <p className="text-[13px] text-gray-500">8 offices, 142 agents</p>
                                </div>
                              </div>
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 10 }}
                                whileTap={{ scale: 0.95 }}
                                className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-md"
                              >
                                <Building2 className="w-6 h-6 text-white" />
                              </motion.div>
                            </motion.div>

                            {/* Stats Row */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.7 }}
                              className="grid grid-cols-2 gap-4"
                            >
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                              >
                                <div className="text-[28px] font-bold text-gray-900 leading-none mb-1.5">
                                  <CounterAnimation value={47} duration={1500} />
                                </div>
                                <div className="text-[13px] text-gray-500">Active orders</div>
                              </motion.div>
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm"
                              >
                                <div className="text-[28px] font-bold text-gray-900 leading-none mb-1.5">
                                  <CounterAnimation value={15} duration={1500} />
                                </div>
                                <div className="text-[13px] text-gray-500">Approved</div>
                              </motion.div>
                            </motion.div>

                            {/* Recent Activity */}
                            <div className="space-y-3.5">
                              <div className="flex items-center justify-between px-0.5 pt-0.5">
                                <h4 className="font-bold text-[15px] text-gray-900">Recent Activity</h4>
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                >
                                  <RefreshCw className="w-4 h-4 text-blue-600" />
                                </motion.div>
                              </div>

                              {[
                                {
                                  office: "Downtown Office",
                                  action: "Order approved",
                                  time: "Just now",
                                  iconColor: "text-green-500",
                                },
                                {
                                  office: "Westside Branch",
                                  action: "New install request",
                                  time: "5 min ago",
                                  iconColor: "text-blue-500",
                                },
                                {
                                  office: "North District",
                                  action: "Inventory updated",
                                  time: "12 min ago",
                                  iconColor: "text-purple-500",
                                },
                              ].map((activity, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, x: -20, scale: 0.95 }}
                                  animate={{ opacity: 1, x: 0, scale: 1 }}
                                  transition={{ delay: 0.9 + index * 0.1 }}
                                  whileHover={{
                                    x: 4,
                                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                                    scale: 1.02,
                                  }}
                                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
                                >
                                  <div className="flex items-center gap-3.5">
                                    <motion.div
                                      whileHover={{ scale: 1.1, rotate: 5 }}
                                      className={`w-10 h-10 rounded-xl flex items-center justify-center`}
                                    >
                                      <Building2 className={`w-8 h-8 ${activity.iconColor}`} />
                                    </motion.div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-gray-900 truncate">
                                        {activity.office}
                                      </p>
                                      <p className="text-xs text-gray-500">{activity.action}</p>
                                    </div>
                                    <div className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-500 bg-gray-100 whitespace-nowrap">
                                      {activity.time}
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>

                            {/* Bottom Actions */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.3 }}
                              className="grid grid-cols-2 gap-3.5 pt-2"
                            >
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-blue-600 text-white p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-md"
                              >
                                <UserCheck className="w-4 h-4" />
                                Approvals
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-white border border-gray-200 text-gray-500 p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-sm"
                              >
                                <BarChart3 className="w-4 h-4" />
                                Reports
                              </motion.button>
                            </motion.div>
                          </div>

                          {/* Home Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[110px] h-[4px] bg-black/80 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Enhanced Floating Notifications */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="absolute -right-12 top-16 bg-white rounded-2xl shadow-2xl p-2 max-w-52 z-20"
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg"
                      >
                        <CheckCircle className="w-4 h-4 text-white" />
                      </motion.div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Order Approved!</p>
                        <p className="text-xs text-gray-600">Downtown Office</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Additional Floating Element - Inventory Alert */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: -20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    transition={{ delay: 2, duration: 0.5 }}
                    className="absolute -left-10 bottom-12 rounded-2xl shadow-2xl px-3.5 py-2.5 max-w-52 z-20"
                    style={{ background: 'linear-gradient(90deg, #073399 0%, #236DD9 100%)' }}
                  >
                    <div className="flex items-center gap-2.5 text-white">
                      <motion.div
                        animate={{
                          rotate: [0, 15, -15, 0],
                          scale: [1, 1.1, 1],
                        }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <CheckCircle className="w-3.5 h-3.5 text-white" />
                      </motion.div>
                      <div>
                        <p className="text-xs font-bold leading-tight">Low Inventory alert</p>
                        <p className="text-[11px] opacity-90 leading-tight">12 Sign Remaining</p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Floating Action Button */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 2.5, duration: 0.5 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute -bottom-1 right-6 w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-2xl z-20 cursor-pointer"
                    style={{ background: "#f58b01" }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 180, 360] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    >
                      <Settings className="w-6 h-6 text-white" />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Outcomes & Benefits Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Outcomes & Benefits
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Transform your brokerage operations with measurable results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {outcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-6 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-14 h-14 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={outcome.icon} size={30} alt={outcome.title} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{outcome.title}</h3>
                  <p className="text-gray-600">{outcome.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Before/After Comparison */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                The RealEsy Difference
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                See how RealEsy transforms brokerage operations from chaos to clarity.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Before */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-red-50 p-8 rounded-2xl border-2 border-red-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <X className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Before RealEsy</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Scattered vendor communication",
                    "Lost signs and unknown inventory",
                    "Hours reconciling invoices",
                    "Inconsistent branding across offices",
                    "No visibility into costs or orders",
                    "Email chaos for design files",
                    "Agents ordering without oversight",
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* After */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-green-50 p-8 rounded-2xl border-2 border-green-200"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">With RealEsy</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "Unified vendor management",
                    "Real-time inventory tracking",
                    "Automated billing & payments",
                    "Locked branding standards",
                    "Complete cost visibility",
                    "Secure WorkDrive file system",
                    "Full oversight & approval workflows",
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              className="absolute w-8 h-8"
            >
              {i % 3 === 0 ? (
                <Building2 className="w-full h-full text-white" />
              ) : i % 3 === 1 ? (
                <Users className="w-full h-full text-white" />
              ) : (
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Your Brokerage, Simplified.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              RealEsy is the all-in-one real estate brokerage management software designed to
              streamline operations, enforce branding, and keep agents, vendors, and offices
              connected — on web and mobile.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white w-full sm:w-auto"
                >
                  Get Started
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
